import React, { useEffect, useMemo, useState } from "react";
import FormSection from "../components/FormSection";
import {
  EDES_MILESTONE_CATALOG_ID,
  EDES_MILESTONE_PROJECTS,
  EDES_MILESTONE_ROWS,
  sortEdesMilestoneRows,
} from "../data/edesMilestoneCatalog";
import {
  fetchProjectMilestoneCatalog,
  fetchProjectMilestoneRun,
  saveProjectMilestoneRun,
} from "../services/projectMilestoneClient";
import {
  deriveOpsMilestoneStatus,
  getOpsMilestoneColor,
  todayIsoDate,
} from "../utils/opsMilestoneStatus";
import "../styles/utilities.css";

function countStatuses(rows, targetStatus) {
  return rows.filter(row => row.status === targetStatus).length;
}

export default function EdesMilestoneTracker() {
  const [projectId, setProjectId] = useState(EDES_MILESTONE_PROJECTS[0]?.project_id || "Edes Court");
  const [catalogId] = useState(EDES_MILESTONE_CATALOG_ID);
  const [catalogRows, setCatalogRows] = useState(() => sortEdesMilestoneRows(EDES_MILESTONE_ROWS));
  const [runDate, setRunDate] = useState(todayIsoDate());
  const [completedDates, setCompletedDates] = useState({});
  const [notes, setNotes] = useState({});
  const [loadingCatalog, setLoadingCatalog] = useState(false);
  const [loadingRun, setLoadingRun] = useState(false);
  const [runError, setRunError] = useState("");
  const [saveStatus, setSaveStatus] = useState("");
  const [dataSource, setDataSource] = useState("catalog");

  useEffect(() => {
    let abort = false;

    async function loadCatalog() {
      setLoadingCatalog(true);
      try {
        const res = await fetchProjectMilestoneCatalog({ catalog_id: catalogId });
        if (abort) return;
        setCatalogRows(sortEdesMilestoneRows(res.items || EDES_MILESTONE_ROWS));
      } catch (error) {
        if (!abort) {
          console.warn("edes milestone catalog load error", error);
          setCatalogRows(sortEdesMilestoneRows(EDES_MILESTONE_ROWS));
        }
      } finally {
        if (!abort) setLoadingCatalog(false);
      }
    }

    loadCatalog();
    return () => {
      abort = true;
    };
  }, [catalogId]);

  useEffect(() => {
    let abort = false;

    async function loadRun() {
      if (!projectId || !runDate) return;
      setLoadingRun(true);
      setRunError("");
      try {
        const data = await fetchProjectMilestoneRun({ project_id: projectId, run_date: runDate });
        if (abort) return;

        if (data?._loaded_from_latest && data.run_date && data.run_date !== runDate) {
          setRunDate(data.run_date);
        }

        setCompletedDates(data?.completed_dates || {});
        setNotes(data?.notes || {});
        setDataSource(data?._data_source || "api");
      } catch (error) {
        if (!abort) {
          setRunError(error?.message || String(error));
          setCompletedDates({});
          setNotes({});
        }
      } finally {
        if (!abort) setLoadingRun(false);
      }
    }

    loadRun();
    return () => {
      abort = true;
    };
  }, [projectId, runDate]);

  const todayIso = useMemo(() => todayIsoDate(), []);

  const displayRows = useMemo(() => {
    const sorted = sortEdesMilestoneRows(catalogRows);
    let lastSection = "";

    return sorted.map(row => {
      const taskId = String(row.task_id ?? row.task ?? "");
      const completedDate = completedDates[taskId] || "";
      const status = deriveOpsMilestoneStatus({
        plannedFinish: row.planned_finish,
        completedDate,
        todayIso,
      });

      const display = {
        ...row,
        task_id: taskId,
        completed_date: completedDate,
        status,
        color: getOpsMilestoneColor(status),
        isNewSection: row.section !== lastSection,
      };

      lastSection = row.section;
      return display;
    });
  }, [catalogRows, completedDates, todayIso]);

  const summary = useMemo(() => {
    return {
      total: displayRows.length,
      done: displayRows.filter(row => Boolean(row.completed_date)).length,
      overdue: countStatuses(displayRows, "overdue_incomplete"),
      ahead: countStatuses(displayRows, "completed_ahead"),
    };
  }, [displayRows]);

  function toggleDone(taskId, checked) {
    setCompletedDates(prev => ({
      ...prev,
      [taskId]: checked ? (prev[taskId] || runDate) : "",
    }));
  }

  function setCompletedOn(taskId, value) {
    setCompletedDates(prev => ({
      ...prev,
      [taskId]: value,
    }));
  }

  function setNote(taskId, value) {
    setNotes(prev => ({
      ...prev,
      [taskId]: value,
    }));
  }

  async function handleSave() {
    try {
      setSaveStatus("Saving...");
      const payload = {
        project_id: projectId,
        run_date: runDate,
        catalog_id: catalogId,
        completed_dates: Object.fromEntries(
          Object.entries(completedDates).filter(([, value]) => Boolean(value))
        ),
        notes: Object.fromEntries(
          Object.entries(notes).filter(([, value]) => String(value || "").trim())
        ),
      };

      const result = await saveProjectMilestoneRun(payload);
      setDataSource(result?._data_source || "api");
      setSaveStatus("Saved");
    } catch (error) {
      console.error("project milestone save error", error);
      setSaveStatus("Save failed");
    } finally {
      setTimeout(() => setSaveStatus(""), 2000);
    }
  }

  return (
    <div className="page">
      <div className="page-header">
        <h2>Edes Milestone Tracker</h2>
        <p>Project-level milestone tracking for pre-construction, site development, and future building rollout.</p>
      </div>

      <FormSection title="Context">
        <div className="grid-2">
          <label>
            Project
            <select value={projectId} onChange={e => setProjectId(e.target.value)}>
              {EDES_MILESTONE_PROJECTS.map(project => (
                <option key={project.project_id} value={project.project_id}>
                  {project.project_id}
                </option>
              ))}
            </select>
          </label>
          <label>
            Run Date
            <input type="date" value={runDate} onChange={e => setRunDate(e.target.value)} />
          </label>
        </div>

        <div className="grid-2">
          <label>
            Catalog
            <div style={readonlyValueStyle}>{catalogId}</div>
          </label>
          <label>
            Data Source
            <div style={readonlyValueStyle}>{dataSource === "local" ? "Local fallback" : "API / catalog"}</div>
          </label>
        </div>

        <div style={summaryGridStyle}>
          <div style={summaryCardStyle}>
            <div style={summaryLabelStyle}>Completed</div>
            <div style={summaryValueStyle}>{summary.done} / {summary.total}</div>
          </div>
          <div style={summaryCardStyle}>
            <div style={summaryLabelStyle}>Overdue</div>
            <div style={summaryValueStyle}>{summary.overdue}</div>
          </div>
          <div style={summaryCardStyle}>
            <div style={summaryLabelStyle}>Ahead</div>
            <div style={summaryValueStyle}>{summary.ahead}</div>
          </div>
        </div>

        <div className="actions" style={{ justifyContent: "flex-start" }}>
          <button className="btn primary" type="button" onClick={handleSave} disabled={!projectId || !runDate}>
            {saveStatus === "Saving..." ? "Saving..." : "Save Milestone Run"}
          </button>
          {saveStatus && saveStatus !== "Saving..." && (
            <div style={saveStatusStyle}>{saveStatus}</div>
          )}
        </div>
      </FormSection>

      <div className="banner info" style={{ marginTop: 16 }}>
        Status colors follow the OPS progress rule set: overdue incomplete is pink, completed ahead stays blue only until the original finish date passes, and completed on-time or late is purple.
      </div>

      {loadingRun && (
        <div className="banner info" style={{ marginTop: 8 }}>
          Loading saved milestone run...
        </div>
      )}
      {loadingCatalog && (
        <div className="banner info" style={{ marginTop: 8 }}>
          Loading milestone catalog...
        </div>
      )}
      {runError && (
        <div className="banner warn" style={{ marginTop: 8 }}>
          {runError}
        </div>
      )}

      <FormSection title="Milestone Grid">
        <div className="table-wrap" style={{ marginTop: 12 }}>
          <table className="table" style={{ minWidth: 1120 }}>
            <thead>
              <tr>
                <th>Done</th>
                <th>Task #</th>
                <th>Milestone</th>
                <th>Duration</th>
                <th>Planned Start</th>
                <th>Planned Finish</th>
                <th>Completed On</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              {displayRows.map(row => (
                <tr
                  key={row.task_id}
                  style={{
                    background: row.color.background,
                    boxShadow: row.isNewSection ? `inset 0 3px 0 ${row.color.border}` : "none",
                  }}
                >
                  <td style={doneCellStyle}>
                    <input
                      type="checkbox"
                      checked={Boolean(row.completed_date)}
                      onChange={e => toggleDone(row.task_id, e.target.checked)}
                    />
                  </td>
                  <td className="text-strong">{row.task_id}</td>
                  <td>
                    <div className="text-strong">{row.operation}</div>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 4 }}>
                      <span style={buildMetaTag("#f3f4f6", "#374151")}>{row.section}</span>
                      <span
                        style={buildMetaTag(
                          row.color.badgeBackground,
                          row.color.badgeColor
                        )}
                      >
                        {row.color.label}
                      </span>
                    </div>
                  </td>
                  <td>{row.duration_days}</td>
                  <td>{row.planned_start}</td>
                  <td>{row.planned_finish}</td>
                  <td>
                    <input
                      type="date"
                      value={row.completed_date || ""}
                      onChange={e => setCompletedOn(row.task_id, e.target.value)}
                    />
                  </td>
                  <td>
                    <input
                      type="text"
                      value={notes[row.task_id] || ""}
                      onChange={e => setNote(row.task_id, e.target.value)}
                      placeholder="Notes"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </FormSection>
    </div>
  );
}

function buildMetaTag(background, color) {
  return {
    display: "inline-flex",
    alignItems: "center",
    borderRadius: 999,
    padding: "0.18rem 0.55rem",
    fontSize: "0.72rem",
    fontWeight: 600,
    background,
    color,
  };
}

const readonlyValueStyle = {
  padding: "0.45rem 0.6rem",
  minHeight: 36,
  display: "flex",
  alignItems: "center",
  backgroundColor: "#f3f4f6",
  border: "1px solid #d1d5db",
  borderRadius: 6,
  color: "#111827",
};

const summaryGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
  gap: 12,
};

const summaryCardStyle = {
  border: "1px solid #d1d5db",
  borderRadius: 10,
  background: "#f8fafc",
  padding: 12,
};

const summaryLabelStyle = {
  fontSize: "0.72rem",
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  color: "#6b7280",
};

const summaryValueStyle = {
  marginTop: 6,
  fontSize: "1.35rem",
  fontWeight: 700,
  color: "#111827",
};

const doneCellStyle = {
  width: 56,
  textAlign: "center",
  verticalAlign: "middle",
};

const saveStatusStyle = {
  display: "flex",
  alignItems: "center",
  color: "#374151",
  fontSize: "0.9rem",
  fontWeight: 600,
};
